import { Component, OnInit, OnDestroy, signal, computed } from "@angular/core";

import { Router, ActivatedRoute } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { LucideAngularModule, Pen, Plus } from "lucide-angular";
import { Subject, takeUntil } from 'rxjs';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { Chatbot } from '../../core/models/user.model';
import { DataTableComponent, TableColumn, TableAction, TableConfig } from '../../shared/components/data-table';
import { USER_TABLE_CONFIG } from '../user-management/config/table.config';
import { ChatbotManagementService } from "../../core/services/chatbot-management.service";;
import { ChatbotEditModalComponent } from "./components/chatbot-edit-modal.component";
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';

@Component({
  selector: 'app-chatbot-management',
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatCardModule,
    LucideAngularModule,
    DataTableComponent,
    MatSnackBarModule,
    MatCheckboxModule,
    PageHeaderComponent
],
  templateUrl: './chatbot-management.component.html',
  styleUrls: ['./chatbot-management.component.scss']
})
export class ChatbotManagementComponent implements OnInit, OnDestroy {
    // íconos de lucide angular
    readonly Pen = Pen;
    readonly Plus = Plus;
    
    readonly destroy$ = new Subject<void>();
    
    // State signals
    chatbots = signal<Chatbot[]>([]);
    loading = signal<boolean>(false);
    activeOnly = signal<boolean>(false);

    // Definición de columnas - se inicializa en el constructor
    tableColumns: TableColumn<Chatbot>[] = [
        {
            key: 'name',
            header: 'Nombre',
            type: 'text',
            sortable: true,
        },
        {
            key: 'code',
            header: 'Código',
            type: 'text',
            searchable: true,
            sortable: true,
        },
        {
            key: 'isActive',
            header: 'Estado',
            type: 'badge',
            badgeConfig: {
                getValue: (chatbot: Chatbot) => chatbot.isActive,
                trueLabel: 'Activo',
                falseLabel: 'Inactivo',
                trueClass: 'bg-green-100 text-green-800',
                falseClass: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
            },
            sortable: true,
        },
        {
            key:'updatedAt',
            header: 'Modificado Por',
            type: 'text',
            formatter: (value: any, chatbot: Chatbot) => {
                const date = new Date(chatbot.updatedAt).toLocaleDateString('es-CL', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                });
                const by = chatbot.updatedBy ? ` (Por: ${chatbot.updatedBy})` : '';
                return date + by;
            },
            sortable: true,
        }
    ];

    // Definición de configuración de tabla
    tableConfig: TableConfig = {
        searchable: true,
        searchPlaceholder: 'Ingrese código o nombre para buscar chatbots',
        pageable: true,
        defaultPageSize: USER_TABLE_CONFIG.defaultPageSize,
        pageSizeOptions: USER_TABLE_CONFIG.pageSizeOptions,
        showFirstLastButtons: USER_TABLE_CONFIG.showFirstLastButtons,
        emptyMessage: 'No se encontraron chatbots',
        emptyIcon: 'search',
        loadingMessage: 'Cargando chatbots...',
        resultsSingular: 'chatbot',
        resultsPlural: 'chatbots'
    };


    // Definición de acciones
    tableActions: TableAction<Chatbot>[] = [
        {
            icon: 'Pen',
            color: 'primary',
            tooltip: 'Editar Chatbot',
            callback: (chatbot) => this.openEditModal(chatbot),
            lucideIcon: this.Pen
        }
    ];

    constructor(
        readonly chatbotService: ChatbotManagementService,
        readonly dialog: MatDialog,
        readonly router: Router,
        readonly route: ActivatedRoute,
        readonly snackBar: MatSnackBar
    ) {}

    ngOnInit(): void {
        this.loadData();
        
        // Listen to query params to open modals
        this.route.queryParams
        .pipe(takeUntil(this.destroy$))
        .subscribe(params => {
            const action = params['action'];
            
            if (action === 'new') {
                this.openCreateModalFromRoute();
            } else if (action === 'edit') {
                const chatbotId = params['chatbotId'];
            if (chatbotId) {
                this.openEditModalFromRoute(+chatbotId);
            }
            }
        });
    }
    
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Open create modal from route - called when navigating to /chatbot-management?action=new
     */
    private openCreateModalFromRoute(): void {
        // Wait for data to load before opening modal
        if (this.loading()) {
            setTimeout(() => this.openCreateModalFromRoute(), 100);
            return;
        }

        const dialogRef = this.dialog.open(ChatbotEditModalComponent, {
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            panelClass: 'custom-dialog-container',
            data: { 
                chatbot: null
            }
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                // Clear query params to return to base route
                this.router.navigate([], { 
                    relativeTo: this.route,
                    queryParams: {}
                });
                
                if (result) {
                    this.loadData();
                }
            });
    }

    /**
     * Open edit modal from route - called when navigating to /chatbot-management?action=edit&chatbotId=X
     */
    private openEditModalFromRoute(chatbotId: number): void {
        // Wait for data to load before opening modal
        if (this.loading()) {
            setTimeout(() => this.openEditModalFromRoute(chatbotId), 100);
            return;
        }

        // Find chatbot by ID
        const chatbot = this.chatbots().find(c => c.chatbotId === chatbotId);
        
        if (!chatbot) {
            this.showError('Chatbot no encontrado');
            // Clear query params
            this.router.navigate([], { 
                relativeTo: this.route,
                queryParams: {}
            });
            return;
        }

        const dialogRef = this.dialog.open(ChatbotEditModalComponent, {
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            panelClass: 'custom-dialog-container',
            data: { 
                chatbot: { ...chatbot }
            }
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                // Clear query params to return to base route
                this.router.navigate([], { 
                    relativeTo: this.route,
                    queryParams: {}
                });
                
                if (result) {
                    // Reload chatbots after successful update
                    this.loadData();
                }
            });
    }
    
    // función para crear un chatbot
    openCreateModal(): void {
        // Navigate using query params to avoid component recreation
        this.router.navigate([], { 
        relativeTo: this.route,
        queryParams: { action: 'new' },
        queryParamsHandling: 'merge'
        });
    }

    // función para editar un chatbot
    openEditModal(chatbot: Chatbot): void {
        // Navigate using query params to avoid component recreation
        this.router.navigate([], { 
          relativeTo: this.route,
          queryParams: { action: 'edit', chatbotId: chatbot.chatbotId },
          queryParamsHandling: 'merge'
        });
    }


    // Función personalizada de búsqueda
    searchFunction = (chatbot: Chatbot, term: string): boolean => {
        const lowerTerm = term.toLowerCase();
        if (chatbot.name.toLowerCase().includes(lowerTerm) ||
            chatbot.code.toLowerCase().includes(lowerTerm)) {
            return (true)
        }        
        return (false)
      };

    // Función que carga los datos de chatbots
    loadData(): void {
        this.loading.set(true);

        this.chatbotService.getAllChatbots()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
            next: (chatbots) => {
                this.chatbots.set(chatbots);
                this.loading.set(false);
            },
            error: () => {
                this.loading.set(false);
                this.showError('Error al cargar los chatbots');
            }
            });
    }
    
    // Función para filtrar chatbots activos
    filterActiveChatbots = computed(() => {
        const chatbots = this.chatbots();
        const onlyActive = this.activeOnly();

        if (onlyActive) {
            return chatbots.filter(bot => bot.isActive);
        }
        return chatbots;
    });

    /**
     * Show success message
     */
    private showSuccess(message: string): void {
        this.snackBar.open(message, 'Cerrar', {
            duration: 3000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['success-snackbar']
        });
    }

/**
   * Show error message
   */
    private showError(message: string): void {
        this.snackBar.open(message, 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
        });
    }
}

